import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Dashboard.css';
import EventCard, { EventSummary, buildEventSummaries } from './EventCard';
import { Bet } from './BetCard';
import { fetchBets, fetchDistinctBookmakerTitles, fetchDistinctSportTitles } from '../services/analytics';
import { useActiveSubscription } from '../hooks/useActiveSubscription';

const DEFAULT_PAGE_SIZE = 30;

const Events: React.FC = () => {
  const [filters, setFilters] = useState({
    sport: '',
    bookmakers: [] as string[],
    homeTeam: '',
    awayTeam: '',
    fromTime: '',
    toTime: '',
    evFilter: 'all'
  });

  const [bets, setBets] = useState<Bet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const [bookmakerOptions, setBookmakerOptions] = useState<string[]>([]);
  const [sportOptions, setSportOptions] = useState<string[]>([]);

  const { hasActiveSub } = useActiveSubscription();

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const target = e.target as HTMLSelectElement & HTMLInputElement;
    const { name, value } = target;

    if (name === 'bookmakers') {
      const isMulti = (target as HTMLSelectElement).multiple;
      if (isMulti) {
        const values = Array.from((target as HTMLSelectElement).selectedOptions).map(o => o.value);
        setFilters(prev => ({ ...prev, bookmakers: values }));
      } else {
        const values = value ? [value] : [];
        setFilters(prev => ({ ...prev, bookmakers: values }));
      }
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
    setOffset(0);
  };

  // Load distinct options for dropdowns
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [bk, sp] = await Promise.all([
        fetchDistinctBookmakerTitles(),
        fetchDistinctSportTitles()
      ]);
      if (cancelled) return;
      if (!bk.error) setBookmakerOptions(bk.data);
      if (!sp.error) setSportOptions(sp.data);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Load bets matching filters (upcoming by default)
  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);

      const { data, error } = await fetchBets({
        isLive: false,
        evFilter: filters.evFilter as any,
        sportTitles: filters.sport ? [filters.sport] : undefined,
        bookmakerTitles: filters.bookmakers && filters.bookmakers.length > 0 ? filters.bookmakers : undefined,
        homeTeam: filters.homeTeam,
        awayTeam: filters.awayTeam,
        fromTime: filters.fromTime ? new Date(filters.fromTime).toISOString() : undefined,
        toTime: filters.toTime ? new Date(filters.toTime).toISOString() : undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
        sort: 'commence_time_asc',
      });

      if (isCancelled) return;

      if (error) {
        setError(error);
        setBets([]);
        setHasMore(false);
      } else {
        const page = (data ?? []) as Bet[];
        setBets((prev) => (offset === 0 ? page : [...prev, ...page]));
        setHasMore(page.length === DEFAULT_PAGE_SIZE);
      }
      setLoading(false);
    };

    load();
    return () => { isCancelled = true; };
  }, [filters.evFilter, filters.sport, filters.bookmakers, filters.homeTeam, filters.awayTeam, filters.fromTime, filters.toTime, offset]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting) {
        setOffset(prev => prev + DEFAULT_PAGE_SIZE);
      }
    }, { root: null, rootMargin: '0px 0px 400px 0px', threshold: 0.01 });
    observer.observe(sentinel);
    return () => { observer.disconnect(); };
  }, [hasMore, loading]);

  const eventSummaries: EventSummary[] = useMemo(() => {
    const summaries = buildEventSummaries(bets);
    // Mirror landing page: show events that have at least two bets
    return summaries.filter(s => s.numBets >= 2);
  }, [bets]);

  const resetFilters = () => {
    setFilters({
      sport: '',
      bookmakers: [],
      homeTeam: '',
      awayTeam: '',
      fromTime: '',
      toTime: '',
      evFilter: 'all'
    });
    setOffset(0);
  };

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Events</h1>
          <p>Search and filter upcoming events</p>
        </div>

        <div className="upcoming-bets">
          <div className="filters-section">
            <h3>Filters</h3>
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="sport">Sport</label>
                <select id="sport" name="sport" value={filters.sport} onChange={handleFilterChange}>
                  <option value="">All Sports</option>
                  {sportOptions.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="bookmakers">Bookmaker{hasActiveSub ? 's' : ''}</label>
                <select
                  id="bookmakers"
                  name="bookmakers"
                  multiple={hasActiveSub}
                  value={hasActiveSub ? filters.bookmakers : (filters.bookmakers[0] ?? '')}
                  onChange={handleFilterChange}
                >
                  {!hasActiveSub && <option value="">All Bookmakers</option>}
                  {bookmakerOptions.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="homeTeam">Home Team</label>
                <input type="text" id="homeTeam" name="homeTeam" placeholder="Search home team..." value={filters.homeTeam} onChange={handleFilterChange} />
              </div>

              <div className="filter-group">
                <label htmlFor="awayTeam">Away Team</label>
                <input type="text" id="awayTeam" name="awayTeam" placeholder="Search away team..." value={filters.awayTeam} onChange={handleFilterChange} />
              </div>

              <div className="filter-group">
                <label htmlFor="fromTime">Event Start (From)</label>
                <input type="datetime-local" id="fromTime" name="fromTime" value={filters.fromTime} onChange={handleFilterChange} />
              </div>

              <div className="filter-group">
                <label htmlFor="toTime">Event Start (To)</label>
                <input type="datetime-local" id="toTime" name="toTime" value={filters.toTime} onChange={handleFilterChange} />
              </div>

              <div className="filter-group">
                <label htmlFor="evFilter">Expected Value</label>
                <select id="evFilter" name="evFilter" value={filters.evFilter} onChange={handleFilterChange}>
                  <option value="all">All</option>
                  <option value="positive">Positive EV</option>
                  <option value="negative">Negative EV</option>
                </select>
              </div>
            </div>
          </div>

          {error && (
            <div className="no-results"><p>Failed to load events: {error}</p></div>
          )}

          {loading && offset === 0 && (
            <div className="no-results"><p>Loading events…</p></div>
          )}

          {!error && (eventSummaries.length > 0 || (loading && offset > 0)) && (
            <div className="bets-grid">
              {eventSummaries.map(es => (
                <EventCard key={es.eventId} summary={es} />
              ))}
            </div>
          )}

          {!loading && !error && eventSummaries.length === 0 && (
            <div className="no-results">
              <p>No events match your current filters.</p>
              <button className="btn btn-secondary" onClick={resetFilters}>Clear Filters</button>
            </div>
          )}

          {!loading && !error && eventSummaries.length > 0 && hasMore && (
            <div className="no-results">
              <button className="btn btn-secondary" onClick={() => setOffset(prev => prev + DEFAULT_PAGE_SIZE)}>Load More</button>
            </div>
          )}

          {loading && offset > 0 && (
            <div className="no-results"><p>Loading more…</p></div>
          )}

          <div ref={sentinelRef} />
        </div>
      </div>
    </div>
  );
};

export default Events;


